// @ts-strict-ignore
import { useEffect, useMemo, useState } from 'react'
import { useDebounce } from '@node-space/hooks'
import { type RowsPerPageType } from '@node-space/ui-components'
import { SEARCH_INPUT_DEBOUNCE_TIME } from 'constants/General'
import { useCurrencyFilterOptions } from 'hooks/queries/Customers/useCurrencyFilterOptions'
import { useGetCustomersWalletsQuery } from 'hooks/queries/Customers/useGetCustomersWallets'
import { usePaginationPagingPersist } from 'hooks/usePaginationPagingPersist'
import type { CustomerWalletFilterProps } from 'pages/Customers/CustomerView/CustomerWallets'
import {
  CustomerWalletStatusType,
  LedgerWalletType,
  type CustomerWalletStatus,
  type LedgerType,
} from 'types/customers'

export const useCustomerWalletFilters = (customerReference: string) => {
  const [activeTab, setActiveTab] = useState<LedgerType>(LedgerWalletType.FIAT)
  const [walletNameSearch, setWalletNameSearch] = useState<string>('')
  const debouncedWalletNameSearch = useDebounce(walletNameSearch, SEARCH_INPUT_DEBOUNCE_TIME)
  const [currencyFilter, setCurrencyFilter] = useState<string>('')
  const [statusFilterValues, setStatusFilterValues] = useState<CustomerWalletStatus[]>([
    CustomerWalletStatusType.ACTIVE,
    CustomerWalletStatusType.INACTIVE,
  ])

  const [page, setPage] = useState<number>(0)
  const [rowsPerPage, setRowsPerPage] = useState<RowsPerPageType>(10)

  const { currencyOptions, isFetchingCurrencies } = useCurrencyFilterOptions(activeTab)

  const defaultStatusFilter: CustomerWalletStatus[] = [
    CustomerWalletStatusType.ACTIVE,
    CustomerWalletStatusType.INACTIVE,
  ]

  const {
    data: { content: customerWallets, pagination: customerWalletPagination } = {},
    isFetching: isFetchingCustomerWallets,
    isError: isErrorFetchingCustomerWallets,
    refetch: fetchCustomerWallets,
  } = useGetCustomersWalletsQuery({
    customerReference,
    page,
    size: rowsPerPage,
    currencyType: activeTab,
    ...(currencyFilter && { currencyCodeSearch: currencyFilter }),
    ...(debouncedWalletNameSearch && { searchTerm: debouncedWalletNameSearch }),
    ...(!!statusFilterValues.length && { statuses: statusFilterValues }),
  })

  const { paging, showPagination } = usePaginationPagingPersist(customerWalletPagination)

  const { data: fiatCountData, isFetching: isFetchingFiatCount } = useGetCustomersWalletsQuery({
    customerReference,
    currencyType: LedgerWalletType.FIAT,
    size: 100,
    page: 0,
    statuses: defaultStatusFilter,
  })

  const { data: cryptoCountData, isFetching: isFetchingCryptoCount } = useGetCustomersWalletsQuery({
    customerReference,
    currencyType: LedgerWalletType.CRYPTO,
    size: 100,
    page: 0,
    statuses: defaultStatusFilter,
  })

  const fiatWalletCount = fiatCountData?.pagination?.total
  const cryptoWalletCount = cryptoCountData?.pagination?.total
  const isFetchingWalletCounts = isFetchingFiatCount || isFetchingCryptoCount

  const handleTabChange = (tab: LedgerType) => {
    setActiveTab(tab)
    setCurrencyFilter('')
    setWalletNameSearch('')
    setPage(0)
  }

  const handlePaginationChange = (newPage: number) => setPage(newPage)

  const handleRowsPerPageChange = (newRowsPerPage: RowsPerPageType) => {
    setRowsPerPage(newRowsPerPage)
    setPage(0)
  }

  useEffect(() => {
    setPage(0)
  }, [debouncedWalletNameSearch, currencyFilter, statusFilterValues])

  const filterProps: CustomerWalletFilterProps = useMemo(
    () => ({
      activeTab,
      onTabChange: handleTabChange,
      walletNameSearch,
      isSearching: !!debouncedWalletNameSearch,
      onChangeWalletNameSearch: setWalletNameSearch,
      currencyFilter,
      onCurrencyFilterChange: setCurrencyFilter,
      currencyOptions,
      isFetchingCurrencies,
      statusFilterValues,
      onStatusFilterChange: setStatusFilterValues,
      fiatWalletCount,
      cryptoWalletCount,
      isFetchingWalletCounts,
    }),
    [
      activeTab,
      walletNameSearch,
      debouncedWalletNameSearch,
      currencyFilter,
      currencyOptions,
      isFetchingCurrencies,
      statusFilterValues,
      fiatWalletCount,
      cryptoWalletCount,
      isFetchingWalletCounts,
    ]
  )

  const isPaginationVisible = !isErrorFetchingCustomerWallets && !!paging && !showPagination

  return {
    filterProps,
    customerWallets,
    isFetchingCustomerWallets,
    isErrorFetchingCustomerWallets,
    fetchCustomerWallets,
    pagination: {
      page,
      rowsPerPage,
      paging,
      isVisible: isPaginationVisible,
      onChangePage: handlePaginationChange,
      onChangeRowsPerPage: handleRowsPerPageChange,
    },
  }
}
